/**
 * CAT Profile Service
 *
 * API service for CAT Profile (Phase 2) operations:
 * eligibility checking, profile generation, and retrieval
 */

import { useAuthStore } from '@vsat/shared/store/authStore';

const getApiBaseUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3001/api/v1';
  }
  return `${window.location.origin}/api/v1`;
};

const CAT_PROFILE_BASE = `${getApiBaseUrl()}/career-orientation`;

// ========== Types ==========

export interface SubjectExamScore {
  subject: string;
  subjectName: string;
  attemptCount: number;
  scores: number[];
  average: number;
}

export interface CATEligibilityResult {
  isEligible: boolean;
  caseType: 'CASE_A' | 'CASE_B' | 'CASE_C' | null;
  hasCompletedAssessment: boolean;
  hasFullReportAccess: boolean;
  assessmentId: string | null;
  subjectScores: SubjectExamScore[];
  totalSubjectsAttempted: number;
  maxAttemptsOnSingleSubject: number;
  caseAProgress: {
    current: number;
    required: number;
    met?: boolean;
  };
  caseBProgress: {
    current: number;
    required: number;
    subject?: string | null;
    met?: boolean;
  };
  totalAttempts: number;
  unifiedProgress: {
    current: number;
    required: number;
  };
}

export interface MajorRecommendation {
  majorId: string;
  majorName: string;
  primarySubject: string;
  primarySubjectName: string;
  threshold: number;
  userScore: number;
  passed: boolean;
}

export interface GroupRecommendation {
  groupCode: string;
  groupName: string;
  majors: MajorRecommendation[];
  totalMajors: number;
  passedMajors: number;
}

export interface WeakSubject {
  subject: string;
  subjectName: string;
  average: number;
}

export interface VocationalSuggestion {
  groupCode: string;
  groupName: string;
  careers: string[];
}

export interface LowScoreWarning {
  level: 'critical' | 'low' | 'moderate';
  message: string;
  overallAverage: number;
  weakSubjects: WeakSubject[];
  vocationalSuggestions?: VocationalSuggestion[] | null;
}

export interface SubjectChange {
  subject: string;
  subjectName: string;
  previousAverage: number;
  currentAverage: number;
  difference: number;
  trend: 'up' | 'down' | 'stable';
}

export interface ScoreComparison {
  previousAverage: number;
  currentAverage: number;
  difference: number;
  trend: 'up' | 'down' | 'stable';
  previousGeneratedAt: string;
  subjectChanges: SubjectChange[];
}

export interface ScoreHistoryEntry {
  average: number;
  subjectAverages: Record<string, number>;
  generatedAt: string;
}

export interface PersonalityScore {
  groupCode: string;
  groupName: string;
  score: number;
  maxScore: number;
}

export interface CATProfile {
  id: string;
  userId: string;
  assessmentId: string;
  caseType: string;
  educationLevel?: string | null;
  examScores: SubjectExamScore[];
  recommendations: GroupRecommendation[];
  personalityScores?: PersonalityScore[];
  generatedAt: string;
  totalRecommendedMajors: number;
  totalPassedMajors: number;
  lowScoreWarning?: LowScoreWarning | null;
  scoreComparison?: ScoreComparison | null;
  scoreHistory?: ScoreHistoryEntry[];
}

// ========== Service ==========

class CATProfileService {
  private async handleUnauthorized() {
    const { logout } = useAuthStore.getState();
    await logout();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  private getHeaders() {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
    };
  }

  /**
   * Check user's CAT Profile eligibility
   */
  async checkEligibility(): Promise<CATEligibilityResult> {
    const response = await fetch(`${CAT_PROFILE_BASE}/cat-profile/eligibility`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized - Please login again');
    }

    if (!response.ok) {
      throw new Error('Failed to check eligibility');
    }

    return response.json();
  }

  /**
   * Generate CAT Profile
   */
  async generateProfile(assessmentId?: string): Promise<CATProfile> {
    const response = await fetch(`${CAT_PROFILE_BASE}/cat-profile/generate`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ assessmentId }),
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized - Please login again');
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to generate CAT Profile');
    }

    return response.json();
  }

  /**
   * Get existing CAT Profile
   */
  async getProfile(): Promise<CATProfile | null> {
    const response = await fetch(`${CAT_PROFILE_BASE}/cat-profile`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized - Please login again');
    }

    if (!response.ok) {
      throw new Error('Failed to fetch CAT Profile');
    }

    const data = await response.json();
    // API returns { profile: null, message: '...' } when no profile exists
    if (data.profile === null) {
      return null;
    }
    return data;
  }

  /**
   * Download CAT Profile PDF
   */
  async downloadPdf(): Promise<void> {
    const response = await fetch(`${CAT_PROFILE_BASE}/cat-profile/pdf`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized - Please login again');
    }

    if (!response.ok) {
      throw new Error('Failed to download PDF');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Bao-cao-tu-van-chon-nganh-dai-hoc.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Toggle share on  -  returns shareToken
   */
  async shareProfile(): Promise<{ shared: boolean; shareToken: string | null }> {
    const response = await fetch(`${CAT_PROFILE_BASE}/cat-profile/share`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({}),
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }

    if (!response.ok) throw new Error('Failed to share profile');
    return response.json();
  }

  /**
   * Disable share
   */
  async disableShare(): Promise<{ shared: boolean; shareToken: null }> {
    const response = await fetch(`${CAT_PROFILE_BASE}/cat-profile/share`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ disable: true }),
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized');
    }

    if (!response.ok) throw new Error('Failed to disable share');
    return response.json();
  }

  /**
   * Get public profile by share token (no auth needed)
   */
  async getPublicProfile(shareToken: string) {
    const baseUrl =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:3001/api/v1'
        : `${window.location.origin}/api/v1`;

    const response = await fetch(`${baseUrl}/career-orientation/public/cat-profile/${shareToken}`);
    if (!response.ok) return null;
    return response.json();
  }
}

export const catProfileService = new CATProfileService();
